import { vec3 } from 'gl-matrix';

const SHORT_HEX_COLOR_REGEX = /^#([0-9a-fA-F])([0-9a-fA-F])([0-9a-fA-F])$/;
const LONG_HEX_COLOR_REGEX =
  /^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})?$/;
const RGB_COLOR_REGEX =
  /^(?:rgb|rgba)\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(,\s*(\d+(\.\d+)?))?\s*\)$/;

function parseHexSegment(hex: string): number {
  const value = parseInt(hex, 16);

  if (hex.length === 1) {
    return (value << 4) + value;
  }

  return value;
}

const strategies = [
  {
    regex: SHORT_HEX_COLOR_REGEX,
    parse: (r: string, g: string, b: string) => {
      return vec3.fromValues(parseHexSegment(r), parseHexSegment(g), parseHexSegment(b));
    },
  },
  {
    regex: LONG_HEX_COLOR_REGEX,
    parse: (r: string, g: string, b: string) => {
      return vec3.fromValues(parseHexSegment(r), parseHexSegment(g), parseHexSegment(b));
    },
  },
  {
    regex: RGB_COLOR_REGEX,
    parse: (r: string, g: string, b: string) => {
      return vec3.fromValues(parseFloat(r), parseFloat(g), parseFloat(b));
    },
  },
];

export function parseWebColor(color: string): vec3 | null {
  try {
    for (const strategy of strategies) {
      const match = color.match(strategy.regex);

      if (match) {
        return strategy.parse(...(match.slice(1) as [string, string, string]));
      }
    }
  } catch (error) {
    // Return null if the color is invalid
    return null;
  }

  return null;
}

export interface WebColorWithAlpha {
  rgb: vec3;
  /** Opacity from 0 (fully transparent) to 1 (fully opaque). Defaults to 1 when absent. */
  alpha: number;
}

const alphaStrategies = [
  {
    regex: SHORT_HEX_COLOR_REGEX,
    parse: (match: RegExpMatchArray): WebColorWithAlpha => ({
      rgb: vec3.fromValues(
        parseHexSegment(match[1]),
        parseHexSegment(match[2]),
        parseHexSegment(match[3]),
      ),
      alpha: 1,
    }),
  },
  {
    regex: LONG_HEX_COLOR_REGEX,
    parse: (match: RegExpMatchArray): WebColorWithAlpha => ({
      rgb: vec3.fromValues(
        parseHexSegment(match[1]),
        parseHexSegment(match[2]),
        parseHexSegment(match[3]),
      ),
      alpha: match[4] != null ? parseHexSegment(match[4]) / 255 : 1,
    }),
  },
  {
    regex: RGB_COLOR_REGEX,
    parse: (match: RegExpMatchArray): WebColorWithAlpha => ({
      rgb: vec3.fromValues(
        parseFloat(match[1]),
        parseFloat(match[2]),
        parseFloat(match[3]),
      ),
      alpha: match[5] != null ? parseFloat(match[5]) : 1,
    }),
  },
];

/**
 * Parses a CSS colour string into its RGB components and alpha (opacity).
 *
 * Unlike {@link parseWebColor}, this preserves any alpha channel present in an
 * 8-digit hex colour or an `rgba(...)` value, defaulting to fully opaque (`1`)
 * when the colour has no alpha channel.
 */
export function parseWebColorWithAlpha(color: string): WebColorWithAlpha | null {
  try {
    for (const strategy of alphaStrategies) {
      const match = color.match(strategy.regex);

      if (match) {
        return strategy.parse(match);
      }
    }
  } catch (error) {
    // Return null if the color is invalid
    return null;
  }

  return null;
}
